<script setup>
import { initPosDisplayEcho } from '@/plugins/echo'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

// Addendum 16, Modul BY — halaman display pelanggan Mode Papan Antrian
// (tenant-wide, BUKAN per-shift seperti [shiftId].vue). Device ini TIDAK
// PERNAH login staf -- token pairing (dari pos-display/connect, mode
// papan_antrian) di query string adalah satu-satunya kredensial, dikirim
// sebagai header X-Pos-Display-Token ke /broadcasting/auth lewat
// initPosDisplayEcho() -- fungsi Echo yang SAMA dipakai [shiftId].vue,
// tidak ada instance Echo baru (nama channel-lah yang membedakan, bukan
// cara otorisasinya).
//
// CATATAN: sama seperti [shiftId].vue, broadcast real-time belum benar-benar
// aktif di lingkungan dev ini (VITE_PUSHER_APP_KEY belum di-set) --
// initPosDisplayEcho() akan no-op sampai driver push sungguhan diaktifkan.

const route = useRoute('pos-display-queue-board')

const token = route.query.token
const tenantId = route.query.tenant_id

const connectionStatus = ref(token ? 'connecting' : 'no-token')

const preparing = ref([])
const ready = ref([])

const handleQueueStatusUpdated = payload => {
  preparing.value = payload?.preparing ?? []
  ready.value = payload?.ready ?? []
}

onMounted(() => {
  if (!token) return

  const echo = initPosDisplayEcho(token)

  if (!echo) {
    connectionStatus.value = 'realtime-disabled'

    return
  }

  echo.private(`tenant.${tenantId}.queue-board`)
    .subscribed(() => { connectionStatus.value = 'connected' })
    .error(() => { connectionStatus.value = 'rejected' })
    .listen('.QueueStatusUpdated', handleQueueStatusUpdated)
})
</script>

<template>
  <div class="cds-screen">
    <template v-if="connectionStatus === 'no-token' || connectionStatus === 'rejected'">
      <div class="cds-status-card">
        <VIcon icon="ri-error-warning-line" size="56" color="error" class="mb-4" />
        <h4 class="text-h4 mb-2">Tidak Bisa Terhubung</h4>
        <p class="text-body-1 text-medium-emphasis">
          {{ connectionStatus === 'no-token'
            ? 'Token tidak ditemukan. Silakan hubungkan ulang lewat halaman pairing.'
            : 'Otorisasi ditolak — token sudah kedaluwarsa atau toko sudah tidak aktif.' }}
        </p>
      </div>
    </template>

    <template v-else-if="connectionStatus === 'connecting'">
      <div class="cds-status-card">
        <VProgressCircular indeterminate color="primary" size="48" class="mb-4" />
        <h4 class="text-h4">Menghubungkan...</h4>
      </div>
    </template>

    <template v-else-if="connectionStatus === 'realtime-disabled'">
      <div class="cds-status-card">
        <VIcon icon="ri-wifi-off-line" size="56" color="warning" class="mb-4" />
        <h4 class="text-h4 mb-2">Realtime Belum Aktif</h4>
        <p class="text-body-1 text-medium-emphasis">
          Server ini belum dikonfigurasi untuk update langsung. Hubungi admin sistem.
        </p>
      </div>
    </template>

    <!-- ── Papan Antrian — 2 kolom besar & jelas, §4.1 addendum ────────── -->
    <template v-else>
      <div class="queue-board-display">
        <h2 class="text-h3 font-weight-bold mb-8 queue-board-title">
          Papan Antrian
        </h2>

        <div class="queue-board-columns">
          <div class="queue-board-column queue-board-column--preparing">
            <div class="queue-board-column-title">Sedang Diproses</div>
            <div v-if="preparing.length === 0" class="queue-board-empty">-</div>
            <div v-else class="queue-board-numbers">
              <span v-for="n in preparing" :key="n" class="queue-board-number">{{ n }}</span>
            </div>
          </div>

          <VDivider vertical class="queue-board-divider" />

          <div class="queue-board-column queue-board-column--ready">
            <div class="queue-board-column-title">Siap Diambil</div>
            <div v-if="ready.length === 0" class="queue-board-empty">-</div>
            <div v-else class="queue-board-numbers">
              <span v-for="n in ready" :key="n" class="queue-board-number">{{ n }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Layar kiosk pelanggan — SENGAJA warna & ukuran font hardcoded, tidak
   mengikuti theme toggle app, sama pola dengan pos-display/[shiftId].vue. */
.cds-screen {
  min-block-size: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #14161f;
  color: #f5f5f7;
  padding: clamp(16px, 4vw, 48px);
  font-size: clamp(16px, 1.4vw, 22px);
  --v-theme-on-surface: 245, 245, 247;
}

.cds-screen h1,
.cds-screen h2,
.cds-screen h3,
.cds-screen h4,
.cds-screen h5,
.cds-screen h6,
.cds-screen p {
  color: inherit;
}

.cds-status-card {
  text-align: center;
  max-inline-size: 480px;
}

.queue-board-display {
  inline-size: min(1100px, 96vw);
  text-align: center;
}
.queue-board-title {
  letter-spacing: 0.05em;
}

.queue-board-columns {
  display: flex;
  align-items: stretch;
  gap: clamp(16px, 3vw, 48px);
}
.queue-board-column {
  flex: 1;
  min-inline-size: 0;
}
.queue-board-divider {
  border-color: rgba(255, 255, 255, 0.15);
}

.queue-board-column-title {
  font-size: clamp(1.3rem, 2.4vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-block-end: clamp(16px, 2.5vw, 32px);
}
.queue-board-column--preparing .queue-board-column-title {
  color: #f5c65b;
}
.queue-board-column--ready .queue-board-column-title {
  color: #6ee7a0;
}

.queue-board-numbers {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(10px, 1.5vw, 20px);
}
.queue-board-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-inline-size: clamp(64px, 8vw, 110px);
  padding-block: clamp(8px, 1.2vw, 16px);
  padding-inline: clamp(12px, 1.5vw, 20px);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1;
}
.queue-board-column--ready .queue-board-number {
  background: #1f7a4c;
}

.queue-board-empty {
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  color: #a3a6b8;
  padding-block: clamp(24px, 4vw, 48px);
}
</style>
